import {inject, Injectable, NgZone, signal, WritableSignal} from '@angular/core';
import {Experiment, ExperimentClient} from '@amplitude/experiment-js-client';
import {PlatformService} from "./platform.service";
import {isNative} from "../utils";
import {createIs} from "typia";

const apartmentPageStyles = ['no-section', 'eligible-section', 'eligible-section-2', 'eligible-section-3', 'eligible-section-4'] as const;
const landingPageOptions = ['initial', 'animated'] as const;
const lotteryCarouselOptions = ['initial', 'animated'] as const;
const titleOptions = ['made-easy'] as const;
const priceOptions = ['regular-monthly', 'promotion-monthly', '15-monthly', '10-monthly', '20-monthly'] as const;
const landingPageSubtext = ['auto-apply'] as const;
type LandingPageHeroLine = {
  text: string;
  delay: number;
  className?: string;
};

type LandingPageHeroImageType = 'fixed' | 'carousel';

interface LandingPageHeroImage {
  type: LandingPageHeroImageType;
  src: string;
  carouselVariant?: 'app-screenshot-carousel-3';
}

interface LandingPageHeaderCopy {
  primary: string;
  secondary?: string;
  primaryDelay: number;
  secondaryDelay: number;
  secondaryClass?: string;
}

type LandingPageSectionOrder = ('search' | 'reviews' | 'how-it-works' | 'next-home' | 'why' | 'data-protection' | 'faq')[];
const landingPageSectionOrderDefault = ['search',  'reviews', 'how-it-works', 'next-home', 'why', 'data-protection', 'faq'] satisfies LandingPageSectionOrder;

interface LandingPageAboveFoldExperiment {
  srTitle: string;
  header: LandingPageHeaderCopy;
  bodyLines: LandingPageHeroLine[];
  signup: {
    text: string;
    delay: number;
  };
  heroImage: LandingPageHeroImage;
  showFeaturedIn: boolean;
}

const defaultLandingPageAboveFold: LandingPageAboveFoldExperiment = {
  srTitle: 'Homey NYC Housing Lottery Made Easy',
  header: {
    primary: 'Housing Lottery',
    secondary: "<span class=\"text-orange-gradient\">Made Easy</span>",
    primaryDelay: 0,
    secondaryDelay: 800
  },
  bodyLines: [
    {text: 'Auto-apply for every', delay: 1800},
    {text: 'NYC Housing Lottery', delay: 2500},
    {text: 'in 7 minutes', delay: 3200},
  ],
  signup: {
    text: 'Try for $0',
    delay: 3800,
  },
  heroImage: {
    type: 'fixed',
    src: '/assets/landing-page/new-landing-page-image-B.webp',
  },
  showFeaturedIn: true,
};
const textEffect = ['none', 'shimmer'] as const;
const freeTrialOptions = ['14-days', '7-days'] as const;
const checkoutPageTitle = ['initial', 'card'] as const;
const checkoutPageStyle = ['review', 'initial', "mobile"] as const;
const checkoutPageExtra = ['nothing', 'image-carousel'] as const;
const checkoutType = ['custom', 'stripe'] as const;
const landingPageButtonOptions = ['try-for-0'] as const;
const transition = ['animated', 'none'] as const;
const textStyle = ['initial', 'bolder', "bolder-all-titles", "bolder-all"] as const;
const footerStyles = ['initial', 'with-email-list'] as const;
const reviewsStyles = ['initial', 'kris'] as const;
const reviewPromptOptions = ['none', 'dialog'] as const;
const appIntroductionOptions = ['eligible-page', "none"] as const;
const backtrackTitles = ['default', 'friendly'] as const;
const stripeABGroups = ['a', 'b'] as const;
const paymentLocationGroups = ['original', 'sunk-cost', 'sunk-cost-no-trial'] as const;
const defaultBillingMode = ['monthly', 'annual'] as const;
type TitleOption = typeof titleOptions[number];
type PriceOption = typeof priceOptions[number];
type FreeTrialOption = typeof freeTrialOptions[number];
type LandingPageOption = typeof landingPageOptions[number];
type ApartmentPageStyle = typeof apartmentPageStyles[number];
type CheckoutPageTitle = typeof checkoutPageTitle[number];
type CheckoutPageStyle = typeof checkoutPageStyle[number];
type CheckoutType = typeof checkoutType[number];
type LandingPageButtonOption = typeof landingPageButtonOptions[number];
type TextStyle = typeof textStyle[number];
type FooterStyle = typeof footerStyles[number];
type ReviewsStyle = typeof reviewsStyles[number];
type ReviewPrompt = typeof reviewPromptOptions[number];
type CheckoutPageExtra = typeof checkoutPageExtra[number];
type AppIntroduction = typeof appIntroductionOptions[number];
type LandingPageSubtext = typeof landingPageSubtext[number];
type TextEffect = typeof textEffect[number];
type LotteryCarousel = typeof lotteryCarouselOptions[number];
type Transition = typeof transition[number];
type BacktrackTitle = typeof backtrackTitles[number];
type StripeABGroup = typeof stripeABGroups[number];
type PaymentLocationGroupVariant = typeof paymentLocationGroups[number];
type DefaultBillingMode = typeof defaultBillingMode[number];
interface YearlyPromotionOverride {
  label: string;
  originalPrice: string;
  percentOffLabel: string;
  labelColor: string;
}

interface PromotionHeader {
  text: string;
  link: string;
  color: string;
  enabled: boolean;
}

type EventFeedType = {
  enabled: boolean;
  title: string;
  showLiveIndicator: boolean;
  showEmoji: boolean;
  hideLandingPageImage: boolean;
}

type PrivacyPageExperiment = {
  enabled: boolean;
  title: string;
};

type NativeHeroImageConfig = {
  src: string;
  position: {
    left?: string;
    right?: string;
    top?: string;
    bottom?: string;
  };
  aspectRatio: string;
  width: string;
  height?: string;
};

type NativeLandingPageExperiment = {
  heroImages: NativeHeroImageConfig[];
  title: string;
  subtitle: string;
  buttonText: string;
};

export const nativeLandingPageDefaults: NativeLandingPageExperiment = {
  heroImages: [
    {
      src: '/assets/images/housing-lottery/williamsburg-wharf-a1-photo-0.png',
      position: {
        top: '5rem',
        left: '2rem',
      },
      aspectRatio: '3/2',
      width: '7rem',
    },
    {
      src: '/assets/images/housing-lottery/williamsburg-wharf-a1-photo-5.png',
      position: {
        top: '11.5rem',
        left: '5rem',
      },
      aspectRatio: '3/2',
      width: '6rem',
    },
    {
      src: '/assets/images/housing-lottery/williamsburg-wharf-a1-photo-4.png',
      position: {
        top: '7rem',
        right: '2.5rem',
      },
      aspectRatio: '3/2',
      width: '8rem',
    }
  ],
  title: 'Housing Lottery <br><span class="text-orange-gradient">Made Easy</span>',
  subtitle: 'Auto-apply to all NYC Housing Lotteries<br> in just 7 minutes.',
  buttonText: 'Get started',
};

@Injectable({
  providedIn: 'root'
})
export class ABService {
  public readonly platformService = inject(PlatformService);
  private readonly ngZone = inject(NgZone);

  public readonly titleOption = signal<TitleOption | undefined>(undefined);
  public readonly landingPageButton = signal<LandingPageButtonOption>('try-for-0');
  public readonly priceOption = signal<PriceOption>('15-monthly');
  public readonly reviewsStyle = signal<ReviewsStyle>('initial');
  public readonly reviewPrompt = signal<ReviewPrompt>('dialog');
  public readonly freeTrialOption = signal<FreeTrialOption>('7-days');
  public readonly apartmentPageStyle = signal<ApartmentPageStyle>("no-section");
  public readonly landingPageOption = signal<LandingPageOption | undefined>(undefined);
  public readonly lotteryCarousel = signal<LotteryCarousel>('initial');
  public readonly footerStyle = signal<FooterStyle>('initial');
  public readonly textStyle = signal<TextStyle>('initial');
  public readonly checkoutPageTitle = signal<CheckoutPageTitle>('card');
  public readonly checkoutPageStyle = signal<CheckoutPageStyle>('initial');
  public readonly checkoutPageExtra = signal<CheckoutPageExtra>('nothing');
  public readonly checkoutType = signal<CheckoutType>('custom');
  public readonly appIntroduction = signal<AppIntroduction>('none');
  public readonly landingPageSubtext = signal<LandingPageSubtext>('auto-apply');
  public readonly textEffect = signal<TextEffect>('none');
  public readonly transition = signal<Transition>('animated');
  public readonly backtrackTitle = signal<BacktrackTitle>('default');
  public readonly stripeABGroup = signal<StripeABGroup>('a');
  public readonly paymentLocationGroup = signal<PaymentLocationGroupVariant>('original');
  public readonly defaultBillingMode = signal<DefaultBillingMode>('monthly');
  public readonly yearlyPromotionOverride = signal<YearlyPromotionOverride | undefined>(undefined);
  public readonly nativeLandingPage = signal<NativeLandingPageExperiment | undefined>(undefined);
  public readonly landingPageAboveFold = signal<LandingPageAboveFoldExperiment | undefined>(undefined);
  public readonly landingPageSectionOrder = signal<LandingPageSectionOrder | undefined>(undefined);
  public readonly promotionHeader = signal<PromotionHeader>({
    text: '<span class="font-semibold">Promotion:</span> Get 50% off annual plans',
    link: '/signup/preferences/1',
    color: "blue",
    enabled: false
  });
  public readonly eventFeed = signal<EventFeedType>({
    enabled: false,
    title: 'Recent selections',
    showLiveIndicator: true,
    showEmoji: true,
    hideLandingPageImage: false,
  });
  public readonly privacyPage = signal<PrivacyPageExperiment>({
    enabled: true,
    title: 'Privacy & security',
  });

  public async initialize() {
    if (this.platformService.isBrowser) {
      this.ngZone.runOutsideAngular(
        () => this.initializeAsync()
      )
    }

  }

  private async initializeAsync() {
    let experiment
    try {
      experiment = Experiment.initializeWithAmplitudeAnalytics(
        'ee272489d5e5f2b06e130780e666240e'
      );
      console.log('Experiment init');
      console.time('Experiment fetch');
      await experiment.fetch();
      console.timeEnd('Experiment fetch');
    } catch (e) {
      console.error('Failed to initialize ABService', e);
    }
    this.parseVariantWithDefault(experiment, 'title-option', titleOptions, this.titleOption, 'made-easy');
    this.parseVariant(experiment, 'price-option', priceOptions, this.priceOption);
    this.parseVariant(experiment, 'free-trial-option', freeTrialOptions, this.freeTrialOption);
    this.parseVariant(experiment, 'checkout-page-title', checkoutPageTitle, this.checkoutPageTitle);
    this.parseVariant(experiment, 'checkout-page-style', checkoutPageStyle, this.checkoutPageStyle);
    this.parseVariant(experiment, 'checkout-type', checkoutType, this.checkoutType);
    this.parseVariant(experiment, 'landing-page-button', landingPageButtonOptions, this.landingPageButton);
    this.parseVariant(experiment, 'text-style', textStyle, this.textStyle);
    this.parseVariantWithDefault(experiment, 'apartment-page-style', apartmentPageStyles, this.apartmentPageStyle, 'no-section');
    this.parseVariant(experiment, 'footer-style', footerStyles, this.footerStyle);
    this.parseVariant(experiment, 'reviews-style', reviewsStyles, this.reviewsStyle);
    this.parseVariant(experiment, 'reviews', reviewPromptOptions, this.reviewPrompt);
    this.parseVariant(experiment, 'app-introduction', appIntroductionOptions, this.appIntroduction);
    this.parseVariant(experiment, 'checkout-page-extra', checkoutPageExtra, this.checkoutPageExtra);
    this.parseVariant(experiment, 'landing-page-subtext', landingPageSubtext, this.landingPageSubtext);
    this.parseJsonVariantWithDefault(
      experiment,
      'landing-page-above-the-fold',
      this.landingPageAboveFold,
      createIs<LandingPageAboveFoldExperiment>(),
      defaultLandingPageAboveFold,
    );
    this.parseVariantWithDefault(experiment, 'landing-page-option', landingPageOptions, this.landingPageOption, 'initial');
    this.parseVariant(experiment, 'lottery-carousel', lotteryCarouselOptions, this.lotteryCarousel);
    this.parseVariant(experiment, 'text-effect', textEffect, this.textEffect);
    this.parseVariant(experiment, 'transition', transition, this.transition);
    this.parseVariant(experiment, 'backtrack-title', backtrackTitles, this.backtrackTitle);
    this.parseVariant(experiment, 'stripe-ab-group', stripeABGroups, this.stripeABGroup);
    this.parseVariant(experiment, 'default-billing-mode', defaultBillingMode, this.defaultBillingMode);
    this.parseJsonVariant(experiment, 'yearly-promotion-override', this.yearlyPromotionOverride, createIs<YearlyPromotionOverride>());
    this.parseJsonVariant(experiment, 'promotion-banner-2', this.promotionHeader, createIs<PromotionHeader>());
    this.parseJsonVariant(experiment, 'event-feed-2', this.eventFeed, createIs<EventFeedType>());
    this.parseJsonVariant(experiment, 'privacy-page', this.privacyPage, createIs<PrivacyPageExperiment>());
    this.parseJsonVariantWithDefault(experiment, 'landing-page-order-improved', this.landingPageSectionOrder, createIs<LandingPageSectionOrder>(), landingPageSectionOrderDefault);
    this.parseJsonVariantWithDefault(
      experiment,
      'native-landing-page',
      this.nativeLandingPage,
      createIs<NativeLandingPageExperiment>(),
      nativeLandingPageDefaults,
    );


    if(!isNative) {
      this.parseVariant(experiment, 'payment-location-group', paymentLocationGroups, this.paymentLocationGroup);
    }

    if(this.textStyle() === 'bolder-all-titles') {
      document.body.classList.add('ab-bold');
    } else if(this.textStyle() === 'bolder-all') {
      document.body.classList.add('ab-bold-all');
    }

    if(this.transition() !== 'animated') {
      document.documentElement.classList.add('vt-off');
    } else {
      document.documentElement.classList.remove('vt-off');
    }

    if(this.textEffect() === 'shimmer') {
      document.documentElement.style.setProperty("--animate-shimmer", "running");
    }
  }

  private parseVariant<T>(experiment: ExperimentClient | undefined,
                          variantKey: string,
                          options: readonly T[],
                          writeableSignal: WritableSignal<T>) {
    try {
      if (!experiment) {
        return;
      }
      const override = (window as any).abOverrides;
      if (override && override[variantKey]) {
        for (const option of options) {
          if (override[variantKey] === option) {
            writeableSignal.set(override[variantKey]);
            return;
          }
        }
      }

      const variant = experiment.variant(variantKey);

      for (const option of options) {
        const value = variant.value;
        if (value === option) {
          writeableSignal.set(option);
          return;
        }
      }
    } catch (e) {
      console.error(`Failed to parse variant ${variantKey}`, e);
    }
  }

  private parseVariantWithDefault<T>(
    experiment: ExperimentClient | undefined,
    variantKey: string,
    options: readonly T[],
    writeableSignal: WritableSignal<T>,
    defaultValue: T) {
    try {
      const override = (window as any).abOverrides;
      if (override && override[variantKey]) {
        for (const option of options) {
          if (override[variantKey] === option) {
            writeableSignal.set(override[variantKey]);
            return;
          }
        }
      }

      if (!experiment) {
        console.log("No experiment found, setting default value:", defaultValue);
        writeableSignal.set(defaultValue);
        return;
      }
      const variant = experiment.variant(variantKey);

      for (const option of options) {
        const value = variant.value;
        if (value === option) {
          console.log(`Variant ${variantKey} found, setting value:`, option);
          writeableSignal.set(option);
          return;
        }
      }

      console.log(`No matching variant found for ${variantKey}, setting default value:`, defaultValue);

      writeableSignal.set(defaultValue);
    } catch (e) {
      console.error(`Failed to parse variant ${variantKey}`, e);
      writeableSignal.set(defaultValue);
    }
  }

  private parseJsonVariant<T>(
    experiment: ExperimentClient | undefined,
    variantKey: string,
    writeableSignal: WritableSignal<T | undefined>,
    isFn: (input: unknown) => input is T
  ) {
    try {
      const override = (window as any).abOverrides;
      if (override && override[variantKey]) {
        if (isFn(override[variantKey])) {
          writeableSignal.set(override[variantKey]);
          console.log(`Variant ${variantKey} override found, setting value:`, override[variantKey]);
          return;
        }
      }

      if (!experiment) {
        return;
      }

      const variant = experiment.variant(variantKey);
      const payload = variant.payload;

      if (payload === undefined || payload === null) {
        console.error("Missing payload for JSON variant", variantKey);
        return;
      }

      if (isFn(payload)) {
        writeableSignal.set(payload);
        console.log(`Variant ${variantKey} found, setting value:`, payload);
        return;
      }

      console.error("Parsing JSON variant", variantKey, payload);
    } catch (e) {
      console.error(`Failed to parse variant ${variantKey}`, e);
    }
  }

  private parseJsonVariantWithDefault<T>(
    experiment: ExperimentClient | undefined,
    variantKey: string,
    writeableSignal: WritableSignal<T | undefined>,
    isFn: (input: unknown) => input is T,
    defaultValue: T
  ) {
    try {
      const override = (window as any).abOverrides;
      if (override && override[variantKey] && isFn(override[variantKey])) {
        writeableSignal.set(override[variantKey]);
        return;
      }

      if (!experiment) {
        writeableSignal.set(defaultValue);
        return;
      }

      const variant = experiment.variant(variantKey);
      const payload = variant.payload;

      if (payload === undefined || payload === null) {
        writeableSignal.set(defaultValue);
        return;
      }

      if (isFn(payload)) {
        writeableSignal.set(payload);
        return;
      }

      console.error("Parsing JSON variant", variantKey, payload);
      writeableSignal.set(defaultValue);
    } catch (e) {
      console.error(`Failed to parse variant ${variantKey}`, e);
      writeableSignal.set(defaultValue);
    }
  }
}
